import { bunja } from 'bunja';
import { useBunja } from 'bunja/react';
import { useAtomValue } from 'jotai';
import { ReactNode } from 'react';

import { T } from '@flipster/i18n';
import { Tooltip } from '@flipster/react/design-system/tooltip';
import Formatted from '@flipster/react/design-utils/formatted';
import { css } from '@flipster/styled-system/css';
import { hstack, stack } from '@flipster/styled-system/patterns';

import { tpslModalBunja } from '../../state';
import { instrumentBunja, parentPositionBunja } from '../../state/base';
import { tpslModalInputDerivationBunja } from '../state';

export const OrderInfoSection = () => {
  const {
    isTimeTriggerOrder,
    positionType,
    canHaveLiqPrice,
    marginType,
    entryOrOrderPriceAtom,
    liqPriceAtom,
    pricePrecisionAtom,
    quoteCurrencyAtom,
    quoteCurrencyPrecisionAtom,
    estimatedPnlAtom,
    estimatedPnlPercentAtom,
  } = useBunja(orderInfoSectionBunja);

  const entryOrOrderPrice = useAtomValue(entryOrOrderPriceAtom);
  const liqPrice = useAtomValue(liqPriceAtom);
  const pricePrecision = useAtomValue(pricePrecisionAtom);
  const quoteCurrency = useAtomValue(quoteCurrencyAtom);
  const quoteCurrencyPrecision = useAtomValue(quoteCurrencyPrecisionAtom);
  const estimatedPnl = useAtomValue(estimatedPnlAtom);
  const estimatedPnlPercent = useAtomValue(estimatedPnlPercentAtom);
  const isCross = marginType === 'Cross';

  return (
    <section
      className={stack({
        gap: 8,
        p: 16,
        rounded: 8,
        borderWidth: 1,
        borderStyle: 'solid',
        borderColor: 'border.default.level-1',
      })}
    >
      <InfoRow
        label={
          <Tooltip.Root placement='right-start'>
            <Tooltip.Trigger asChild>
              <span
                className={css({
                  borderBottomWidth: 1,
                  borderBottomStyle: 'dashed',
                  borderBottomColor: 'border.default.level-3',
                })}
              >
                <T id='Autogen_common.r251126.s_Estimated_PnL' />
              </span>
            </Tooltip.Trigger>
            <Tooltip.Content bottomSheetTitle={<T id='Autogen_common.r251126.s_Estimated_PnL' />}>
              <T id='Autogen_common.r251126.s_This_shows_your_estimated_profit_or_loss_for_the_selected_portion_of_your_position' />
            </Tooltip.Content>
          </Tooltip.Root>
        }
        value={
          <span>
            {isTimeTriggerOrder ? (
              <Formatted.Percent value={estimatedPnlPercent} precision={2} toFixed withPlus withColor />
            ) : (
              <span className={hstack({ gap: 4 })}>
                <Formatted.Number value={estimatedPnl} precision={quoteCurrencyPrecision} toFixed withPlus withColor />
                <Formatted.Percent
                  value={estimatedPnlPercent}
                  precision={2}
                  toFixed
                  withPlus
                  withParentheses
                  withColor
                />
              </span>
            )}
          </span>
        }
      />
      <InfoRow
        label={
          positionType === 'open' ? (
            <T id='Common.r251119.Entry_price' />
          ) : (
            <T id='Autogen_common.r251126.s_Order_price' />
          )
        }
        value={<Formatted.Number value={entryOrOrderPrice} precision={pricePrecision} toFixed suffix={quoteCurrency} />}
      />

      {canHaveLiqPrice && (
        <InfoRow
          label={
            isCross ? (
              <Tooltip.Root placement='right-start'>
                <Tooltip.Trigger asChild>
                  <span
                    className={css({
                      borderBottomWidth: 1,
                      borderBottomStyle: 'dashed',
                      borderBottomColor: 'border.default.level-3',
                    })}
                  >
                    <T id='Position.r250401.Est_liquidation_price' />
                  </span>
                </Tooltip.Trigger>
                <Tooltip.Content bottomSheetTitle={<T id='Position.r250401.Est_liquidation_price' />}>
                  <T id='Trade.r260506.Estimated_liquidation_price_desc' />
                </Tooltip.Content>
              </Tooltip.Root>
            ) : positionType === 'open' ? (
              <T id='Assets.r250401.Liquidation_price' />
            ) : (
              <T id='Position.r250401.Est_liquidation_price' />
            )
          }
          value={
            <Formatted.Number
              value={liqPrice}
              precision={pricePrecision}
              toFixed
              zeroString='-'
              suffix={quoteCurrency}
            />
          }
        />
      )}
    </section>
  );
};

const orderInfoSectionBunja = bunja(() => {
  const { isTimeTriggerOrder } = bunja.use(tpslModalBunja);
  const { positionType, canHaveLiqPrice, marginType, entryOrOrderPriceAtom, liqPriceAtom } =
    bunja.use(parentPositionBunja);
  const { pricePrecisionAtom, quoteCurrencyAtom, quoteCurrencyPrecisionAtom } = bunja.use(instrumentBunja);
  const { estimatedPnlAtom, estimatedPnlPercentAtom } = bunja.use(tpslModalInputDerivationBunja);

  return {
    isTimeTriggerOrder,
    positionType,
    canHaveLiqPrice,
    marginType,
    entryOrOrderPriceAtom,
    liqPriceAtom,
    pricePrecisionAtom,
    quoteCurrencyAtom,
    quoteCurrencyPrecisionAtom,
    estimatedPnlAtom,
    estimatedPnlPercentAtom,
  };
});
orderInfoSectionBunja.debugLabel = 'orderInfoSectionBunja';

interface InfoRowProps {
  label: ReactNode;
  value: ReactNode;
}

const InfoRow = ({ label, value }: InfoRowProps) => {
  return (
    <div className={hstack({ justify: 'space-between', gap: 8 })}>
      <span className={css({ textStyle: 'Text/M/Regular', color: 'content.default.level-3' })}>{label}</span>
      <span className={css({ textStyle: 'Text/M/Monospace/Regular', color: 'content.default.level-5' })}>{value}</span>
    </div>
  );
};
